/**
 * Automation slice (PX-W6.5) — scheduler jobs, the Runtime picker's project
 * options, the manual run outcome and the context-index run state.
 *
 * INV-6 honesty: `SKIPPED` is stored verbatim and rendered as itself — never
 * collapsed into success or failure. The slice also ships no enable/disable
 * action (no route exists) and invents no health or score.
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { selectActiveOrgId, type SessionState } from '@/features/auth/slices/sessionSlice'
import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'

import * as service from './services/automationService.http'
import type {
  ContextIndexRunView,
  JobRunResult,
  ProjectOption,
  SchedulerJobView
} from './types'

/** Preserve the canonical ApiError (the transport already mapped it). */
const rejected = (error: unknown): ApiError => (isApiError(error) ? error : toApiError(error))

const orgKey = (getState: () => unknown): string | null =>
  selectActiveOrgId(getState() as { session: SessionState })

interface ProjectKeyed<T> {
  key: string
  value: T
}

export const fetchSchedulerJobs = createAsyncThunk<
  ProjectKeyed<SchedulerJobView[]>,
  void,
  { rejectValue: ApiError }
>(
  'automation/fetchSchedulerJobs',
  async (_, { getState, rejectWithValue }) => {
    const orgId = orgKey(getState)
    try {
      return { key: orgId ?? 'none', value: await service.automationService.listJobs() }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch(
        (getState() as { automation: AutomationState }).automation.jobsCache,
        orgKey(getState)
      )
  }
)

export const fetchAutomationProjects = createAsyncThunk<
  ProjectOption[],
  void,
  { rejectValue: ApiError }
>(
  'automation/fetchAutomationProjects',
  async (_, { rejectWithValue }) => {
    try {
      return await service.automationService.listProjects()
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch(
        (getState() as { automation: AutomationState }).automation.projectsCache,
        null
      )
  }
)

/** Run one job now. `skipped` is stored verbatim (INV-6). */
export const triggerJob = createAsyncThunk<
  JobRunResult,
  { jobKey: string; projectId: string | null },
  { rejectValue: ApiError }
>('automation/triggerJob', async ({ jobKey, projectId }, { rejectWithValue }) => {
  try {
    return await service.automationService.runJob(jobKey, projectId)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export const fetchContextIndexStatus = createAsyncThunk<
  ProjectKeyed<ContextIndexRunView>,
  string,
  { rejectValue: ApiError }
>(
  'automation/fetchContextIndexStatus',
  async (projectId, { getState, rejectWithValue }) => {
    try {
      return {
        key: `${orgKey(getState) ?? 'none'}:${projectId}`,
        value: await service.automationService.contextIndexStatus(projectId)
      }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (projectId, { getState }) =>
      shouldFetch(
        (getState() as { automation: AutomationState }).automation.contextCache,
        `${orgKey(getState) ?? 'none'}:${projectId}`
      )
  }
)

/**
 * Trigger a manual context-index run. The response is the in-flight RUNNING
 * report; the page polls/fetches the status again to replace it with the
 * completed or failed report.
 */
export const triggerContextIndexRun = createAsyncThunk<
  ContextIndexRunView,
  string,
  { rejectValue: ApiError }
>('automation/triggerContextIndexRun', async (projectId, { rejectWithValue }) => {
  try {
    return await service.automationService.runContextIndex(projectId)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export interface AutomationState {
  jobs: SchedulerJobView[]
  jobsCache: FetchCache
  projects: ProjectOption[]
  projectsCache: FetchCache
  contextRun: ContextIndexRunView | null
  contextCache: FetchCache
  jobResult: JobRunResult | null
  runningJobKey: string | null
  runningIndex: boolean
  /*
   * Per-surface errors (fix-back D2): ONE shared `error` field let a
   * context-index 404 replace the SCHEDULER panel's table with the error
   * panel (and vice versa). Each read thunk owns its own field, and the
   * context 404 (`context.index.not-found` — the project has NEVER been
   * indexed) is not an error at all: it is the panel's empty state.
   */
  jobsError: ApiError | null
  projectsError: ApiError | null
  contextError: ApiError | null
  mutationError: ApiError | null
}

const initialState: AutomationState = {
  jobs: [],
  jobsCache: initialFetchCache,
  projects: [],
  projectsCache: initialFetchCache,
  contextRun: null,
  contextCache: initialFetchCache,
  jobResult: null,
  runningJobKey: null,
  runningIndex: false,
  jobsError: null,
  projectsError: null,
  contextError: null,
  mutationError: null
}

export const automationSlice = createSlice({
  name: 'automation',
  initialState,
  reducers: {
    automationErrorDismissed(state) {
      state.jobsError = null
      state.projectsError = null
      state.contextError = null
      state.mutationError = null
    },
    automationRetryClicked(state) {
      invalidate(state.jobsCache)
      invalidate(state.projectsCache)
      invalidate(state.contextCache)
    },
    jobResultDismissed(state) {
      state.jobResult = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSchedulerJobs.pending, (state) => {
        fetchStarted(state.jobsCache)
        state.jobsError = null
      })
      .addCase(
        fetchSchedulerJobs.fulfilled,
        (state, action: PayloadAction<ProjectKeyed<SchedulerJobView[]>>) => {
          fetchSucceeded(state.jobsCache, action.payload.key)
          state.jobs = action.payload.value
        }
      )
      .addCase(fetchSchedulerJobs.rejected, (state, action) => {
        fetchFailed(state.jobsCache)
        state.jobsError = action.payload ?? null
      })
      .addCase(fetchAutomationProjects.pending, (state) => {
        fetchStarted(state.projectsCache)
        state.projectsError = null
      })
      .addCase(fetchAutomationProjects.fulfilled, (state, action: PayloadAction<ProjectOption[]>) => {
        fetchSucceeded(state.projectsCache, null)
        state.projects = action.payload
      })
      .addCase(fetchAutomationProjects.rejected, (state, action) => {
        fetchFailed(state.projectsCache)
        state.projectsError = action.payload ?? null
      })
      .addCase(triggerJob.pending, (state, action) => {
        state.runningJobKey = action.meta.arg.jobKey
        state.mutationError = null
      })
      .addCase(triggerJob.fulfilled, (state, action: PayloadAction<JobRunResult>) => {
        state.runningJobKey = null
        state.jobResult = action.payload
      })
      .addCase(triggerJob.rejected, (state, action) => {
        state.runningJobKey = null
        state.mutationError = action.payload ?? null
      })
      .addCase(fetchContextIndexStatus.pending, (state) => {
        fetchStarted(state.contextCache)
        state.contextError = null
      })
      .addCase(
        fetchContextIndexStatus.fulfilled,
        (state, action: PayloadAction<ProjectKeyed<ContextIndexRunView>>) => {
          fetchSucceeded(state.contextCache, action.payload.key)
          state.contextRun = action.payload.value
        }
      )
      .addCase(fetchContextIndexStatus.rejected, (state, action) => {
        fetchFailed(state.contextCache)
        /*
         * 404 `context.index.not-found` is the NEVER-INDEXED state, not a
         * failure (the controller's own contract: "that is not an empty
         * index and must not be reported as one" — the panel's empty state
         * says exactly that). Everything else is this surface's own error
         * and never touches the scheduler panel's.
         */
        if (action.payload?.status === 404) {
          state.contextRun = null
          state.contextError = null
          return
        }
        state.contextError = action.payload ?? null
      })
      .addCase(triggerContextIndexRun.pending, (state) => {
        state.runningIndex = true
        state.mutationError = null
      })
      .addCase(
        triggerContextIndexRun.fulfilled,
        (state, action: PayloadAction<ContextIndexRunView>) => {
          state.runningIndex = false
          state.contextRun = action.payload
          // The next status read must be able to replace the RUNNING report.
          invalidate(state.contextCache)
        }
      )
      .addCase(triggerContextIndexRun.rejected, (state, action) => {
        state.runningIndex = false
        state.mutationError = action.payload ?? null
      })
  }
})

export const {
  automationErrorDismissed,
  automationRetryClicked,
  jobResultDismissed
} = automationSlice.actions

/* ── Selectors ─────────────────────────────────────────────────────────── */

interface WithAutomation {
  automation: AutomationState
}

export const selectSchedulerJobs = (s: WithAutomation): SchedulerJobView[] =>
  s.automation.jobs
export const selectSchedulerJobsCache = (s: WithAutomation): FetchCache =>
  s.automation.jobsCache
export const selectAutomationProjects = (s: WithAutomation): ProjectOption[] =>
  s.automation.projects
export const selectContextIndexRun = (s: WithAutomation): ContextIndexRunView | null =>
  s.automation.contextRun
export const selectContextIndexCache = (s: WithAutomation): FetchCache =>
  s.automation.contextCache
export const selectJobResult = (s: WithAutomation): JobRunResult | null =>
  s.automation.jobResult
export const selectRunningJobKey = (s: WithAutomation): string | null =>
  s.automation.runningJobKey
export const selectRunningIndex = (s: WithAutomation): boolean => s.automation.runningIndex
export const selectSchedulerJobsError = (s: WithAutomation): ApiError | null =>
  s.automation.jobsError
export const selectAutomationProjectsError = (s: WithAutomation): ApiError | null =>
  s.automation.projectsError
export const selectContextIndexError = (s: WithAutomation): ApiError | null =>
  s.automation.contextError
export const selectAutomationMutationError = (s: WithAutomation): ApiError | null =>
  s.automation.mutationError
